import { useCallback } from 'react';

import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { Option } from '@sorare/core/src/components/search/Option';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  FilterWidget,
  VIRTUAL_TOGGLE_FILTERS,
} from '@sorare/core/src/lib/filters';

import { useCurrentMarketplaceUserContext } from 'contexts/currentMarketplaceUser';

const FILTER = VIRTUAL_TOGGLE_FILTERS.watchlistFilter;

type Props = {
  visible: boolean;
};

const Filter = ({ visible }: Props) => {
  const { myWatchlists } = useCurrentMarketplaceUserContext() || {};
  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });

  const track = useEvents();
  const onChange = useCallback(
    (id: string) => {
      const isActive = currentRefinement === id;
      const newValue = isActive ? undefined : id;
      setVirtualToggle({
        [FILTER.name]: newValue,
      });

      if (!isActive) {
        track('Use Market Filter', {
          filterName: FILTER.trackingName,
          filterValue: id,
        });
      }
    },
    [currentRefinement, setVirtualToggle, track]
  );

  if (!visible || !myWatchlists?.length) return null;

  return (
    <FilterSection>
      {myWatchlists?.map(({ title, id }) => (
        <Option
          key={idFromObject(id)}
          active={currentRefinement === idFromObject(id)}
          onClick={() => onChange(idFromObject(id))}
          variant="radio"
        >
          {title}
        </Option>
      ))}
    </FilterSection>
  );
};

export const WatchlistFilter: FilterWidget = {
  key: VIRTUAL_TOGGLE_FILTERS.watchlistFilter.name,
  type: 'virtualToggle',
  filter: VIRTUAL_TOGGLE_FILTERS.watchlistFilter,
  Component: props => <Filter {...props} />,
};
